/**
 * Storage Data Adapter
 *
 * Provides unified data loading for Storage components.
 * Follows the same pattern as CPU, Motherboard, and Case adapters:
 * 1. List files (11 essential fields for list views)
 * 2. Detail files (additional specifications, loaded on-demand)
 * 3. Pricing files (regularPrice, currentPrice)
 *
 * Usage:
 * ```typescript
 * const adapter = getStorageDataAdapter()
 * const allStorage = await adapter.getAllItems()           // List view
 * const storageDetail = await adapter.getItemDetail('id')  // Detail view
 * ```
 */

import type { PriceInfo, PricingData } from '../common/GenericDataAdapter'
import type { StorageListItem, StorageDetailModel, StorageModel } from '../../models/storage/storage-model'

/**
 * Storage Data Adapter Class
 *
 * Provides List/Pricing loading and Detail retrieval
 */
export class StorageDataAdapter {
  private readonly basePath = '/data/storage'

  private listCache: Map<string, StorageListItem[]> = new Map()
  private pricingCache: Map<string, PriceInfo> = new Map()
  private pricingLoadTime = 0
  private detailCache: Map<string, StorageDetailModel> = new Map()
  private readonly PRICING_TTL = 300000 // 5 minutes

  /**
   * Load list file (current or discontinued)
   */
  private async loadListFile(status: 'current' | 'discontinued'): Promise<StorageListItem[]> {
    const cacheKey = `storage-${status}-list`

    if (this.listCache.has(cacheKey)) {
      return this.listCache.get(cacheKey)!
    }

    const response = await fetch(`${this.basePath}/storage-${status}-list.json`)
    if (!response.ok) {
      throw new Error(`Failed to load Storage ${status} list: ${response.statusText}`)
    }

    const data: StorageListItem[] = await response.json()
    this.listCache.set(cacheKey, data)
    return data
  }

  /**
   * Load pricing file (current or discontinued)
   */
  private async loadPricingFile(status: 'current' | 'discontinued'): Promise<Map<string, PriceInfo>> {
    const now = Date.now()

    // Check if pricing data is stale
    if (this.pricingCache.size > 0 && (now - this.pricingLoadTime) < this.PRICING_TTL) {
      return this.pricingCache
    }

    const response = await fetch(`${this.basePath}/storage-${status}-prices.json`)
    if (!response.ok) {
      console.warn(`Failed to load Storage ${status} pricing: ${response.statusText}`)
      return new Map()
    }

    const data: PricingData = await response.json()
    const priceMap = new Map<string, PriceInfo>()

    if (data?.prices) {
      Object.entries(data.prices).forEach(([id, price]) => {
        priceMap.set(id, price)
      })
    }

    this.pricingLoadTime = now
    return priceMap
  }

  /**
   * Merge list item with pricing data
   */
  private mergeWithPricing(item: StorageListItem, priceInfo?: PriceInfo): StorageModel {
    return {
      ...item,
      regularPrice: priceInfo?.regularPrice ?? null,
      currentPrice: priceInfo?.currentPrice ?? null
    }
  }

  /**
   * Get all storage items (Current + Discontinued)
   */
  async getAllItems(): Promise<StorageModel[]> {
    const [currentList, discontinuedList, currentPricing, discontinuedPricing] = await Promise.all([
      this.loadListFile('current'),
      this.loadListFile('discontinued'),
      this.loadPricingFile('current'),
      this.loadPricingFile('discontinued')
    ])

    const currentWithPricing = currentList.map(item =>
      this.mergeWithPricing(item, currentPricing.get(item.id))
    )
    const discontinuedWithPricing = discontinuedList.map(item =>
      this.mergeWithPricing(item, discontinuedPricing.get(item.id))
    )

    return [...currentWithPricing, ...discontinuedWithPricing]
  }

  /**
   * Get current storage items only
   */
  async getCurrentItems(): Promise<StorageModel[]> {
    const [listItems, priceMap] = await Promise.all([
      this.loadListFile('current'),
      this.loadPricingFile('current')
    ])

    return listItems.map(item => this.mergeWithPricing(item, priceMap.get(item.id)))
  }

  /**
   * Get discontinued storage items only
   */
  async getDiscontinuedItems(): Promise<StorageModel[]> {
    const [listItems, priceMap] = await Promise.all([
      this.loadListFile('discontinued'),
      this.loadPricingFile('discontinued')
    ])

    return listItems.map(item => this.mergeWithPricing(item, priceMap.get(item.id)))
  }

  /**
   * Get detail data for a specific Storage item (List + Detail + Pricing)
   */
  async getItemDetail(itemId: string): Promise<StorageModel> {
    // 1. Find list item from either current or discontinued
    const [currentList, discontinuedList] = await Promise.all([
      this.loadListFile('current'),
      this.loadListFile('discontinued')
    ])

    const listItem = [...currentList, ...discontinuedList].find(item => item.id === itemId)
    if (!listItem) {
      throw new Error(`Storage not found in list data: ${itemId}`)
    }

    // 2. Get pricing info
    const isDiscontinued = listItem.status === 'Discontinued'
    const priceMap = await this.loadPricingFile(isDiscontinued ? 'discontinued' : 'current')
    const priceInfo = priceMap.get(itemId)

    // 3. Get detail data (optional)
    let detailData = this.detailCache.get(itemId)
    if (!detailData) {
      try {
        const response = await fetch(`${this.basePath}/details/${itemId}.json`)
        if (response.ok) {
          detailData = await response.json() as StorageDetailModel
          this.detailCache.set(itemId, detailData)
        }
      } catch (error) {
        console.warn(`Failed to load Storage detail for ${itemId}:`, error)
      }
    }

    // 4. Merge: List + Pricing + Detail (if available)
    const baseModel = this.mergeWithPricing(listItem, priceInfo)

    if (detailData) {
      return {
        ...baseModel,
        ...detailData
      } as StorageModel
    }

    return baseModel
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.listCache.clear()
    this.pricingCache.clear()
    this.detailCache.clear()
    this.pricingLoadTime = 0
  }
}

// Singleton instance
let adapterInstance: StorageDataAdapter | null = null

/**
 * Get the singleton instance of StorageDataAdapter
 */
export function getStorageDataAdapter(): StorageDataAdapter {
  adapterInstance ??= new StorageDataAdapter()
  return adapterInstance
}

/**
 * Reset the singleton instance (useful for testing)
 */
export function resetStorageDataAdapter(): void {
  adapterInstance = null
}
